import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';

const VerifyEmail = () => {
    usePageTitle('Vérification de l’email');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isVerifying, setIsVerifying] = useState(true);
    const [isVerified, setIsVerified] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const verifyEmail = async () => {
            const token = searchParams.get('token');
            
            if (!token) {
                setError('Token de vérification manquant');
                setIsVerifying(false);
                return;
            }

            try {
                const response = await apiFetch(`${process.env.REACT_APP_API_URL}/users/verify-email/${token}`);
                setIsVerified(true);
                toast.success(response.message || 'Email vérifié avec succès !');
                
                // Rediriger vers la page de connexion après 3 secondes
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } catch (error) {
                setError(error.message || 'Erreur lors de la vérification');
                toast.error('Erreur lors de la vérification de l\'email');
            } finally {
                setIsVerifying(false);
            }
        };

        verifyEmail();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md">
                {isVerifying ? (
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4">
                            <svg className="animate-spin h-8 w-8 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Vérification en cours...
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Veuillez patienter pendant que nous vérifions votre email
                        </p>
                    </div>
                ) : isVerified ? (
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full mb-4">
                            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Email vérifié !
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Votre adresse email a été vérifiée avec succès.
                        </p>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                            <p className="text-sm text-green-700 dark:text-green-300">
                                ✨ Vous pouvez maintenant vous connecter et créer votre première organisation !
                            </p>
                        </div>

                        <Link
                            to="/login"
                            className="inline-block w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Se connecter
                        </Link>
                    </div>
                ) : (
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full mb-4">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Échec de la vérification
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {error}
                        </p>

                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                            <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                                <strong>Le lien de vérification a peut-être expiré.</strong>
                            </p>
                            <p className="text-sm text-red-600 dark:text-red-400">
                                Les liens de vérification sont valables 24 heures. Vous pouvez demander un nouveau lien depuis la page de connexion.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/login"
                                className="flex-1 text-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Retour à la connexion
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyEmail;
